/* SimplifiedManualImportExportPage.js – UPDATED 2025-06-10
-------------------------------------------------------------------------
• Retains all 2025-06-09 functionality (bulk-select, diff grid, filters)
• Adds “Export all sites CSV”  and  “All sites Blocks Req Tx” buttons
• Uses project-scoped importClient for per-site network fetches
------------------------------------------------------------------------- */

import React, { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import Papa from "papaparse";
import { parse as json2csv } from "json2csv";
import cloneDeep from "lodash/cloneDeep";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  LinearProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  FormControlLabel,
  Tooltip,
  TextField,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import GTranslateIcon from "@mui/icons-material/GTranslate";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { useQuery } from "@apollo/client";
import { importClient } from "../apollo/importClient";

import Chapter from "../components/Chapter";
import Topic from "../components/Topic";
import Cover from "../components/Cover";
import Header from "../components/Header";
import ContentBuilder from "../components/ContentBuilder";
import ImportHeader from "../components/importexport/ImportHeader";

import resolveEntry from "../craft/dataResolvers/resolveEntry";
import resolveManual from "../craft/dataResolvers/resolveManual";

import buildExportCsv, { buildExportStructure } from "../utils/buildExportCsv";

import buildBlocksReqTranslationCsv from "../utils/buildBlocksReqTranslationCsv";
import {
  debugLog,
  setPreview,
  setImportExport,
  canonCamelCase,
  normalizeEntryType,
} from "../utils/index";
import { exportToXLIFF } from "../utils/exportToXLIFF";
import { exportToInDesignXML } from "../utils/exportToInDesignXML";

import QUERY_MISSING_ENTRIES from "../craft/dynamicQueries/MissingEntriesQuery";
import QUERY_LIVE_MANUAL_EXPORT from "../craft/dynamicQueries/LiveManualExportQuery";
import QUERY_MANUAL_SITES from "../craft/dynamicQueries/QUERY_MANUAL_SITES";
import QUERY_LIVE_ALL_MANUALS_FOR_SITEID from "../craft/dynamicQueries/QUERY_LIVE_ALL_MANUALS_FOR_SITEID";
import QUERY_MANUALS_LIST_FOR_SITEID from "../craft/dynamicQueries/QUERY_MANUALS_LIST_FOR_SITEID";
import LEGAL_QUERY from "../craft/dynamicQueries/LEGAL_QUERY";
import GLOSSARY_QUERY from "../craft/dynamicQueries/GLOSSARY_QUERY";
import GLOBAL_SETS_QUERY from "../craft/dynamicQueries/GLOBAL_SETS_QUERY";
import SimplifiedDiffContent from "../components/importexport/SimplifiedImportComponents";

/* ───────────────────────── constants / helpers ───────────────────────── */

const FALLBACK_TS = new Date("2025-04-29T00:00:00Z");

const stripNested = e => {
  delete e.chapters;
  delete e.topics;
  delete e.steps;
  delete e.content;
  e.shallow = true; // to avoid warnings in resolveEntry for missing props
  return e;
};

/* Resolve S3 credentials. URL params win over localStorage so support staff
   can override per-session without touching the form. Returns null if any
   of the four required values is missing. */
const getS3Creds = () => {
  const qs =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const read = name =>
    qs.get(name) ||
    (typeof window !== "undefined" ? localStorage.getItem(name) : "") ||
    "";
  const key = read("S3_API_KEY");
  const sec = read("S3_SECRET");
  const reg = read("S3_REGION");
  const buck = read("S3_BUCKET");
  if (!key || !sec || !reg || !buck) return null;
  return { key, sec, reg, buck };
};

const safeTitle = (t = "") =>
  (t ? t.replace(/ /g, "_") : "Untitled") || "Untitled";
const tsStamp = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(
    d.getHours()
  )}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};
const fmt = iso =>
  iso ? new Date(iso).toLocaleString(undefined, { hour12: false }) : "—";

/* CSV ⟶ entry-like object (for diff preview) */
function rowToEntryObject(row, craft) {
  const rawType =
    row.entryType ||
    row.entryTypeHandle ||
    craft?.typeHandle ||
    craft?.__typename ||
    "";

  const baseType = canonCamelCase(normalizeEntryType(rawType));
  const entry = stripNested(cloneDeep(craft || {}));
  Object.assign(entry, {
    id: row.entryId,
    type: baseType,
    userTitle: row.entryUserTitle ?? entry.userTitle,
    title: row.entryUserTitle ?? entry.title,
    shallow: true,
  });
  if (!entry.typeHandle) {
    entry.typeHandle =
      row.entryTypeHandle ||
      craft?.typeHandle ||
      craft?.__typename ||
      `${baseType}_Entry`;
  }
  if (!entry.__typename) entry.__typename = entry.typeHandle;
  if (row.entryContent?.trim()) {
    if (baseType === "calloutBlock") {
      entry.calloutText = row.entryContent;
      delete entry.paragraph;
    } else if (baseType === "glossaryTerm") {
      entry.definition = row.entryContent;
      delete entry.paragraph;
    } else if (baseType === "variable") {
      entry.plainText = row.entryContent;
      delete entry.paragraph;
    } else if (baseType === "richTextInlineImagesBlock") {
      entry.richText = row.entryContent;
      delete entry.paragraph;
    } else {
      entry.paragraph = row.entryContent;
      delete entry.calloutText;
    }
  }
  if (row.calloutType !== undefined) entry.calloutType = row.calloutType;
  if (row.youtubeVideo !== undefined) entry.youtubeVideo = row.youtubeVideo;
  entry._resolved = false;
  return entry;
}

/* tiny download helper */
const download = (name, text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
};

/* CSV parsing */
const parseCsv = (file, ok, bad) =>
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: ({ data }) => ok(data),
    error: bad,
  });

/* Render helper for before/after columns */
function renderEntryComponent(entry, varMap = {}) {
  if (!entry) {
    debugLog(3, "no entry!", entry);
    return <em>—</em>;
  }
  const t = canonCamelCase(entry.type || entry.typeHandle || entry.__typename);
  if (t === "manual" || t === "product")
    return <Chapter {...resolveEntry(entry, 0, varMap)} />; // Chapter instead of Manual because Manual is too large for grid display
  if (t === "chapter") return <Chapter {...resolveEntry(entry, 0, varMap)} />;
  if (t.startsWith("article"))
    return <Topic entry={resolveEntry(entry, 0, varMap)} />;
  if (
    t === "step" ||
    [
      "textBlock",
      "textPlusImageBlock",
      "richTextInlineImagesBlock",
      "calloutBlock",
      "videoBlock",
      "columnComposableBblock",
      "largeImageBlock",
      "fullWidthImageBlock",
    ].includes(t)
  )
    return <ContentBuilder {...resolveEntry(entry, 0, varMap)} isNested />;
  debugLog(3, `renderEntryComponent no matched entry type ${t}!`, entry);
  return <em>— {t}</em>;
}

/* Small status cell */
const StatusCell = ({ row }) => {
  let label = "Unchanged";
  let icon = <ArrowIcon sx={{ opacity: 0.3 }} fontSize="small" />;
  if (row.dup) {
    label = "Duplicate";
    icon = <WarningIcon fontSize="small" />;
  } else if (row.typeMismatch) {
    label = "Type mismatch";
    icon = <ErrorIcon color="error" fontSize="small" />;
  } else if (!row.existsInCraft) {
    label = "Create";
    icon = <WarningIcon color="secondary" fontSize="small" />;
  } else if (row.modified) {
    label = "Modified";
    icon = <ArrowIcon fontSize="small" />;
    if (row.emptyAfterChange) {
      label = "Modified – Empty!";
      icon = <WarningIcon color="secondary" fontSize="small" />;
    }
  }
  if (row.submitted)
    return (
      <Box textAlign="center">
        <CheckIcon sx={{ color: "green" }} fontSize="small" />
        <br />
        <Typography variant="caption">Imported</Typography>
      </Box>
    );
  return (
    <Box textAlign="center">
      {icon}
      <br />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );
};

/* ───────────────────────── table view ───────────────────────── */

const TableView = React.memo(
  ({ rows, onToggle, needsTranslation, varMap = {} }) => (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 700, overflowY: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }} />
            <TableCell sx={{ width: 160 }}>Entry</TableCell>
            <TableCell sx={{ minWidth: 320 }}>Current Content</TableCell>
            <TableCell sx={{ width: 130 }}>Status ▶</TableCell>
            <TableCell sx={{ minWidth: 320 }}>Updated Content</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id} hover>
              <TableCell padding="checkbox" sx={{ width: 60 }}>
                <Checkbox
                  disabled={row.invalid}
                  checked={!!row.selected}
                  onChange={() => onToggle(row._k)}
                />
              </TableCell>
              {/* id / type */}
              <TableCell sx={{ width: 160 }}>
                <Typography variant="caption">{row.entryType}</Typography>
                <br />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {row.entryId}
                </Typography>
                <Tooltip
                  arrow
                  title={
                    <>
                      <div>
                        <strong>Primary-site updated:</strong>{" "}
                        {fmt(row.dateUpdatedPrimarySite)}
                      </div>
                      <div>
                        <strong>Last imported:</strong>{" "}
                        {fmt(row.translationImportTimestampCurrentSite)}
                      </div>
                    </>
                  }
                >
                  {/* icon shows only when translation is needed */}
                  {needsTranslation && needsTranslation(row) ? (
                    <GTranslateIcon
                      fontSize="small"
                      sx={{
                        color: "#f50057",
                        mr: 0.5,
                        verticalAlign: "text-bottom",
                        cursor: "help",
                      }}
                    />
                  ) : (
                    <GTranslateIcon
                      fontSize="small"
                      sx={{
                        opacity: 0.25,
                        mr: 0.5,
                        verticalAlign: "text-bottom",
                        cursor: "help",
                      }}
                    />
                  )}
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 320,
                  p: 0,
                  overflow: "visible",
                  verticalAlign: "top",
                }}
              >
                {renderEntryComponent(row.currentEntry, varMap)}
              </TableCell>
              <TableCell sx={{ width: 130, textAlign: "center" }}>
                <StatusCell row={row} />
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 320,
                  p: 0,
                  overflow: "visible",
                  verticalAlign: "top",
                }}
              >
                {renderEntryComponent(row.updatedEntry, varMap)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
);

const handleError = (errorObj, sourceString = "") => {
  if (!errorObj) return null;
  // Craft spits this when the token is wrong / schema can’t be built
  const fatal = /getNullableType/i.test(errorObj.message);
  if (fatal) {
    localStorage.removeItem("GATSBY_CRAFT_API_EXPORT_TOKEN");
    return (
      <Box>
        <ImportHeader
          title={`Invalid or expired token ${sourceString}`}
          subHeader="Please enter a new API token"
        />
        <Paper sx={{ p: 4, maxWidth: 480, mx: "auto", textAlign: "center" }}>
          <Button
            variant="contained"
            href="/importexport"
            onClick={() => window.location.replace("/importexport")}
          >
            Go back to token screen
          </Button>
        </Paper>
      </Box>
    );
  }
  return null;
};

/**
 * Manual import / export page.
 * – manualId can come either from props *or* from the /importexport/manual/:manualId/... route.
 * – siteId defaults to 1 (English) when it isn’t supplied.
 */
export default function SimplifiedManualImportExportPage({
  data: liveManualData = {},
  siteId: siteIdProp = 1,
  loading: liveLoad = true,
  error: liveError = undefined,
}) {
  if (
    !liveManualData ||
    !liveManualData.entry ||
    !liveManualData.entry ||
    !liveManualData.entry.id ||
    (!liveManualData.entry.siteId && !siteIdProp)
  ) {
    console.log("liveManualData missing", liveManualData);
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }
  setPreview(true); // always display preview tooltips in importexport
  setImportExport(); // always se importExport flag for variable parsing

  let errorReturn = handleError(liveError, "live");
  if (errorReturn) {
    return errorReturn;
  }
  // resolve a manual for variable parsing
  // NOTE: We resolve individual entries from our missing entries query so this is only used for applyVariables to blocks that exist in current manual)
  const manual = resolveManual(
    liveManualData.entry,
    liveManualData.globalVariables || []
  );
  const manualId = manual.id;
  const destSiteId = manual.siteId || siteIdProp;
  const varMap = manual.variableValues;

  const [importRows, setImportRows] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [csvErr, setCsvErr] = useState(null);
  const [filterNeedsTranslation, setFilterNeeds] = useState(false);
  const [filterChanged, setFilterChanged] = useState(false);
  const [fileName, setFileName] = useState("");
  const [siteChoice, setSiteChoice] = useState(siteIdProp);
  const navigateRR = useNavigate(); // ← correct router
  const goToSite = () => {
    if (!siteChoice) return;
    navigateRR(`/importexport/manual/${manualId}/site/${siteChoice}/`);
  };

  /* all-site export flags */
  const [exportAllBusy, setExportAllBusy] = useState(false);
  const [exportAllBlocksBusy, setExportAllBlocksBusy] = useState(false);
  const [exportAllListsBusy, setExportAllListsBusy] = useState(false);
  const [exportAllGlobalsBusy, setExportAllGlobalsBusy] = useState(false);
  const [exportAllLegalBusy, setExportAllLegalBusy] = useState(false);
  const [exportAllGlossaryBusy, setExportAllGlossaryBusy] = useState(false);
  const [exportAllLanguagesBusy, setExportAllLanguagesBusy] = useState(false);

  const [uploadLog, setUploadLog] = useState([]); // [{site, ok, msg}]
  const [logOpen, setLogOpen] = useState(false);
  /* FILE PICKER */
  const onFile = useCallback(e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setUploadBusy(true);
    parseCsv(
      f,
      rows => {
        debugLog("CSV rows", rows.length);
        setImportRows(rows);
        setCsvErr(null);
        setUploadBusy(false);
      },
      err => {
        setCsvErr(err.message);
        setUploadBusy(false);
      }
    );
  }, []);

  // primary site data
  const {
    data: sourceManualData,
    loading: sourceLoad,
    error: sourceError,
  } = useQuery(
    // siteId 1 (primary locale)
    QUERY_LIVE_MANUAL_EXPORT,
    {
      variables: { id: Number(manualId), siteId: 1 },
      fetchPolicy: "network-only", // always load even if in cache
    }
  );
  errorReturn = handleError(sourceError, "source");
  if (errorReturn) {
    return errorReturn;
  }

  /* NEW: sites list for multi-site export */
  const { data: sitesData, loading: sitesLoad, error: sitesError } = useQuery(
    QUERY_MANUAL_SITES,
    {
      variables: { id: Number(manualId) },
    }
  );
  errorReturn = handleError(sitesError, "sites");
  if (errorReturn) {
    return errorReturn;
  }

  /* ids to fetch on destination site */
  const ids = useMemo(() => {
    if (importRows.length) return importRows.map(r => String(r.entryId));
    if (liveManualData?.entry)
      return buildExportStructure(liveManualData.entry).map(r =>
        String(r.entryId)
      );
    return [];
  }, [importRows, liveManualData]);

  const { data: destData, loading: destLoad, error: destError } = useQuery(
    QUERY_MISSING_ENTRIES,
    {
      variables: { ids, siteId: [destSiteId] },
      fetchPolicy: "network-only", // always load even if in cache
    }
  );
  errorReturn = handleError(destError, "destination");
  if (errorReturn) {
    return errorReturn;
  }
  /* helper maps */
  const primaryUpdatedMap = useMemo(() => {
    const m = new Map();
    destData?.dateUpdatedPrimarySiteEntries?.forEach(e =>
      m.set(String(e.id), e.contentDateUpdated)
    );
    return m;
  }, [destData]);

  const craftMap = useMemo(() => {
    const m = new Map();
    destData?.entries?.forEach(e => {
      // ✅ iterate first
      m.set(String(e.id), {
        ...e,
        /* authoritative timestamp from the English site */
        contentDateUpdatedPrimarySite:
          primaryUpdatedMap.get(String(e.id)) || "",

        /* last‐import timestamp for the current locale */
        translationImportTimestampCurrentSite:
          e.translationImportTimestamp || "",
      });
    });
    return m;
  }, [destData, primaryUpdatedMap]);

  const liveFlatMap = useMemo(() => {
    const m = new Map();
    if (!liveManualData?.entry) return m;
    buildExportStructure(liveManualData.entry).forEach(n =>
      m.set(String(n.entryId || n.id), n)
    );
    return m;
  }, [liveManualData]);

  /* dup detection */
  const dupSet = useMemo(() => {
    const seen = new Set();
    const dup = new Set();
    importRows.forEach(r => {
      const id = String(r.entryId);
      if (seen.has(id)) dup.add(id);
      seen.add(id);
    });
    return dup;
  }, [importRows]);

  // helper – mutate only the clicked row, so every other object keeps
  // the same reference and React.memo can skip them.
  const updateRow = (prev, rowKey, patchFn) =>
    prev.map((r, idx) => {
      const key = `${r.entryId}_${idx}`;
      return key === rowKey ? { ...r, ...patchFn(r) } : r;
    });

  const toggle = useCallback(
    rowKey =>
      setImportRows(prev =>
        updateRow(prev, rowKey, r => ({ selected: !r.selected }))
      ),
    []
  );

  /* export-row factory */
  const exportRowToGridRow = (r, idx = 0) => {
    const key = `${r.entryId}_${idx}`;
    return {
      ...r,
      _k: key,
      id: key,
      selected: false,
      modified: false,
      invalid: false,
      submitted: false,
      dup: false,
    };
  };

  /* BUILD rows array */
  const rows = useMemo(() => {
    if (destLoad || liveLoad) return [];

    /* no CSV yet – show current manual rows */
    if (importRows.length === 0 && liveManualData?.entry) {
      return buildExportStructure(liveManualData.entry)
        .map((r, idx) => exportRowToGridRow(r, idx))
        .map(row => {
          const meta = craftMap.get(String(row.entryId));
          const primaryTS = primaryUpdatedMap.get(String(row.entryId)) || "";
          const dUpdated = primaryTS ? new Date(primaryTS) : null;
          const dImported = meta?.translationImportTimestampCurrentSite
            ? new Date(meta.translationImportTimestampCurrentSite)
            : FALLBACK_TS;
          return {
            ...row,
            currentEntry: meta || row,
            existsInCraft: !!meta,
            dateUpdatedPrimarySite: primaryTS,
            translationImportTimestampCurrentSite:
              meta?.translationImportTimestampCurrentSite || "",
            needsTranslation: dUpdated ? dUpdated > dImported : false,
          };
        });
    }

    /* after CSV load */
    return importRows.map((r, idx) => {
      const k = `${r.entryId}_${idx}`;
      const craft = craftMap.get(String(r.entryId));
      const live = liveFlatMap.get(String(r.entryId));
      const dup =
        dupSet.has(String(r.entryId)) &&
        importRows.findIndex(x => x.entryId === r.entryId) !== idx;

      // use the same “manual.variableValues” for _all_ rows
      const currentEntryRaw = craft || live || null;
      const currentEntry = currentEntryRaw
        ? resolveEntry(currentEntryRaw, 0, manual.variableValues)
        : null;

      let updatedEntryRaw = rowToEntryObject(r, currentEntryRaw);

      if (live) {
        updatedEntryRaw = {
          ...updatedEntryRaw, // CSV edits stay on top
          typeHandle:
            live.typeHandle ??
            live.entryTypeHandle ?? // what buildExportStructure gives us
            updatedEntryRaw.typeHandle, // keep existing
          __typename:
            live.__typename ??
            live.entryTypeHandle ?? // let resolver use this too
            updatedEntryRaw.__typename,
          sectionHandle: live.sectionHandle,
          fieldHandle: live.fieldHandle,
        };
      }
      const updatedEntry = updatedEntryRaw
        ? resolveEntry(updatedEntryRaw, idx, manual.variableValues)
        : null;

      const isStepWrapper =
        canonCamelCase(r.entryType) === "step" &&
        craft &&
        canonCamelCase(craft.typeHandle) === "topicArticleStepBuilderBlock";

      /* changes start – use the best live handle (craft ⟶ fallback) */
      const liveHandle =
        craft?.typeHandle || live?.typeHandle || live?.__typename;

      const typeMismatch =
        !isStepWrapper &&
        liveHandle &&
        canonCamelCase(liveHandle) !==
          canonCamelCase(r.entryTypeHandle || r.entryType);

      const blockType = canonCamelCase(r.entryTypeHandle || r.entryType);
      const craftTitle =
        blockType === "glossaryTerm"
          ? craft?.title ?? live?.title ?? ""
          : craft?.userTitle ?? live?.userTitle ?? ""; // glossaryTerm → title, others → userTitle

      const titleModified =
        !isStepWrapper &&
        r.entryUserTitle !== undefined &&
        craftTitle !== r.entryUserTitle;
      /* CHG – use getCkHtml so objects / strings compare safely */
      const currentContentRaw =
        craft?.paragraph ||
        craft?.calloutText ||
        craft?.definition ||
        craft?.plainText ||
        craft?.youtubeVideo;

      const currentContent = (currentContentRaw ?? "").toString().trim();
      const importedContent = (r.entryContent ?? "").toString().trim();

      const contentModified =
        !isStepWrapper &&
        r.entryContent !== undefined &&
        currentContent !== importedContent;
      const modified = titleModified || contentModified;
      const emptyAfter =
        (contentModified && (r.entryContent ?? "").trim() === "") ||
        (titleModified &&
          (!r.entryUserTitle || r.entryUserTitle.trim() === ""));

      const liveTypeHandle = craft?.typeHandle || craft?.__typename;
      const primaryTS = primaryUpdatedMap.get(String(r.entryId)) || "";
      const updatedDate = primaryTS ? new Date(primaryTS) : null;
      const importDate = craft?.translationImportTimestampCurrentSite
        ? new Date(craft.translationImportTimestampCurrentSite)
        : FALLBACK_TS;
      const needsTx = updatedDate ? updatedDate > importDate : false;

      const initialSel =
        r.selected !== undefined
          ? r.selected
          : !dup && modified && !typeMismatch && !emptyAfter;

      return {
        id: k,
        _k: k,
        entryId: r.entryId,
        entryType: isStepWrapper
          ? "Step"
          : liveTypeHandle || r.entryType || canonCamelCase(r.entryTypeHandle),
        sectionHandle: craft?.sectionHandle,
        fieldHandle: craft?.fieldHandle,

        entryUserTitle: r.entryUserTitle,
        entryContent: r.entryContent,
        calloutType: r.calloutType,
        youtubeVideo: r.youtubeVideo,
        exportTimestamp: r.exportTimestamp,

        // preserve ui state when it exists, else fall back to defaults
        selected:
          r.selected !== undefined
            ? r.selected
            : isStepWrapper
            ? false
            : initialSel,
        dup,
        existsInCraft: !!craft,
        existsInManual: !!live,
        typeMismatch: isStepWrapper ? false : typeMismatch,
        modified,
        emptyAfterChange: emptyAfter,
        invalid: isStepWrapper ? true : r.invalid || typeMismatch,
        needsTranslation: needsTx,

        currentEntry,
        updatedEntry,
        submitted: r.submitted || false,
        dateUpdatedPrimarySite: primaryTS,
        translationImportTimestampCurrentSite:
          craft?.translationImportTimestampCurrentSite || "",
        translationImportTimestamp:
          craft?.translationImportTimestampCurrentSite || "",
      };
    });
  }, [
    importRows,
    craftMap,
    liveFlatMap,
    destLoad,
    liveLoad,
    dupSet,
    primaryUpdatedMap,
  ]);

  /* push rows into diff-pane */
  // diffRows no longer needed – just pass rows directly
  // React.useEffect(() => setDiffRows(rows), [rows]);

  /* ───────────────────────── filters & helpers ───────────────────────── */

  const needsTranslation = row => !!row.needsTranslation;
  const isChanged = row => !!row.modified;

  const visibleRowsRaw = filterNeedsTranslation
    ? rows.filter(needsTranslation)
    : rows;
  const visibleRows = filterChanged
    ? visibleRowsRaw.filter(isChanged)
    : visibleRowsRaw;

  /* bulk-action helpers */
  const makeKeySet = predicate =>
    new Set(visibleRows.filter(predicate).map(r => r._k));

  const handleSelectAll = () => {
    const keySet = makeKeySet(() => true);
    setImportRows(prev =>
      prev.map((r, idx) =>
        keySet.has(`${r.entryId}_${idx}`) && !r.invalid
          ? { ...r, selected: true }
          : r
      )
    );
  };
  const handleSelectNone = () => {
    const keySet = makeKeySet(() => true);
    setImportRows(prev =>
      prev.map((r, idx) =>
        keySet.has(`${r.entryId}_${idx}`) && !r.invalid
          ? { ...r, selected: false }
          : r
      )
    );
  };
  const handleSelectModified = () => {
    const keySet = makeKeySet(r => r.modified && !r.invalid);
    setImportRows(prev =>
      prev.map((r, idx) =>
        keySet.has(`${r.entryId}_${idx}`) ? { ...r, selected: true } : r
      )
    );
  };
  const handleSelectNeedsTx = () => {
    const keySet = makeKeySet(r => r.needsTranslation && !r.invalid);
    setImportRows(prev =>
      prev.map((r, idx) =>
        keySet.has(`${r.entryId}_${idx}`) ? { ...r, selected: true } : r
      )
    );
  };

  /* ------------------------------------------------------------------ */
  /*  single-site export handlers                                       */
  /* ------------------------------------------------------------------ */

  const handleCsvExport = () => {
    if (!liveManualData?.entry) return;
    const m = liveManualData.entry;
    download(
      `${safeTitle(m.userTitle)}_${m.id}_${m.language}_${
        m.siteId
      }_${tsStamp()}.csv`,
      buildExportCsv(liveManualData.entry)
    );
  };

  const handleXliffExport = () => {
    if (!liveManualData?.entry || !sourceManualData?.entry) return;
    const m = liveManualData.entry;
    download(
      `${safeTitle(m.userTitle)}_${m.id}_${m.language}_${
        m.siteId
      }_${tsStamp()}.xliff`,
      exportToXLIFF(sourceManualData.entry, liveManualData.entry)
    );
  };

  const handleBlocksReqTranslationExport = () => {
    if (!liveManualData?.entry) return;
    const csv = buildBlocksReqTranslationCsv(rows);
    if (csv) {
      const m = liveManualData.entry;
      download(
        `${safeTitle(m.userTitle)}_${m.id}_${m.language}_${
          m.siteId
        }__blocksReqTranslation_${tsStamp()}.csv`,
        csv
      );
    }
  };

  /* ------------------------------------------------------------------ */
  /*  helpers + handlers for ALL-site exports                            */
  /* ------------------------------------------------------------------ */

  /** asyncPool – light concurrency-limit helper
   *  limit : max jobs in flight (0 ⇒ unlimited)
   *  array : items to iterate
   *  fn    : async (item, index) => any
   *  Returns → Promise<Array<PromiseSettledResult>>
   */
  // utils/asyncPool.js  – iterator-free, ESLint-friendly
  async function asyncPool(limit, list, fn) {
    const results = [];
    const executing = new Set();
    let index = 0; // start at 0

    async function enqueue() {
      // eslint-disable-next-line no-plusplus
      const i = index++; // ← post-increment: first i = 0
      if (i >= list.length) return;

      const p = Promise.resolve()
        .then(() => fn(list[i], i))
        .finally(() => executing.delete(p));

      results.push(p);
      executing.add(p);

      if (limit && executing.size >= limit) {
        await Promise.race(executing);
      }
      await enqueue(); // keep pulling until exhausted
    }

    const starters = Array(Math.min(limit || list.length, list.length))
      .fill(0)
      .map(enqueue);

    await Promise.all(starters);
    return Promise.allSettled(results);
  }

  const handleExportAllSitesCsv = useCallback(async () => {
    if (exportAllBusy || !sitesData?.entries) return;
    setExportAllBusy(true);

    try {
      await asyncPool(0, sitesData.entries, async site => {
        const { data } = await importClient.query({
          query: QUERY_LIVE_MANUAL_EXPORT,
          variables: { id: Number(manualId), siteId: site.siteId },
          fetchPolicy: "network-only",
        });

        const entry = data?.entry;
        if (!entry) return; // nothing to export

        const csv = buildExportCsv(entry);
        download(
          `${safeTitle(entry.userTitle)}_${entry.id}_${site.language}` +
            `_${site.siteId}_${tsStamp()}.csv`,
          csv
        );

        /* polite throttle between downloads (≈ 300 ms) */
        await new Promise(r => setTimeout(r, 300));
      });
    } catch (err) {
      // only fires if asyncPool itself throws (shouldn’t with allSettled)
      console.error("all-sites CSV error:", err);
    } finally {
      setExportAllBusy(false);
    }
  }, [exportAllBusy, sitesData, manualId, importClient]); // add importClient

  const handleExportAllSitesBlocksCsv = useCallback(async () => {
    if (exportAllBlocksBusy || !sitesData?.entries) return;
    if (!sourceManualData?.entry) return; // English manual required
    setExportAllBlocksBusy(true);

    try {
      const targets = sitesData.entries.filter(site => site.siteId !== 1);

      await asyncPool(0, targets, async site => {
        /* 1️⃣ destination manual (fresh) */
        const { data: destResp } = await importClient.query({
          query: QUERY_LIVE_MANUAL_EXPORT,
          variables: { id: Number(manualId), siteId: site.siteId },
          fetchPolicy: "network-only",
        });
        const destEntry = destResp?.entry;
        if (!destEntry) return;

        /* 2️⃣ flatten → rows + ID list */
        const destRows = buildExportStructure(destEntry);
        const idsForQuery = destRows.map(r => Number(r.entryId));
        if (idsForQuery.length === 0) return;

        /* 3️⃣ timestamps from primary site */
        const { data: tsResp } = await importClient.query({
          query: QUERY_MISSING_ENTRIES,
          variables: { ids: idsForQuery, siteId: [site.siteId] },
          fetchPolicy: "network-only",
        });

        const updatedMap = new Map(
          (tsResp?.dateUpdatedPrimarySiteEntries ?? []).map(e => [
            String(e.id),
            e.contentDateUpdated,
          ])
        );

        /* 4️⃣ mark rows that need translation */
        const needsRows = destRows.filter(r => {
          r.contentDateUpdatedPrimarySite =
            updatedMap.get(String(r.entryId)) || "";

          const updatedDate = new Date(r.contentDateUpdatedPrimarySite || 0);
          const importDate = r.translationImportTimestamp
            ? new Date(r.translationImportTimestamp)
            : FALLBACK_TS;

          return updatedDate > importDate;
        });
        if (needsRows.length === 0) return;

        /* 5️⃣ CSV & download */
        const csv = json2csv(needsRows, { unwind: true, flatten: true });
        download(
          `${safeTitle(destEntry.userTitle)}_${destEntry.id}_${site.language}` +
            `_${site.siteId}__blocksReqTranslation_${tsStamp()}.csv`,
          csv
        );
      });
    } catch (err) {
      console.error("all-sites blocks req tx export failed:", err);
    } finally {
      setExportAllBlocksBusy(false);
    }
  }, [
    exportAllBlocksBusy,
    sitesData,
    sourceManualData,
    manualId,
    importClient,
  ]);

  function uniqueSitesFromDropdown(sites) {
    if (!Array.isArray(sites)) return [];

    const seen = new Set();

    return sites.filter(Boolean).filter(site => {
      const key =
        site?.siteId != null
          ? `id:${site.siteId}`
          : `sh:${site?.siteHandle || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /* ⇣⇣⇣  RAW MANUALS ➜ S3  ⇣⇣⇣ */
  // Bundle query is huge — fortrabbit gateway 504s when several pile up.
  // Bump when the upstream timeout is raised or the query is split.
  const BUNDLE_QUERY_CONCURRENCY = 1;
  const BUNDLE_QUERY_RETRY_DELAY_MS = 3000;
  const handleUploadAllSitesRawManualsToS3 = async () => {
    if (exportAllBusy || !sitesData?.entries) return;

    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    setExportAllBusy(true);
    const log = [];
    setUploadLog([]);
    setLogOpen(true);
    const pushLog = entry => {
      log.push(entry);
      setUploadLog([...log]); // new ref → React re-renders the dialog
    };

    const s3 = new S3Client({
      region: reg,
      credentials: { accessKeyId: key, secretAccessKey: sec },
    });

    const total = sitesData.entries.length;
    let processed = 0;

    try {
      await asyncPool(BUNDLE_QUERY_CONCURRENCY, sitesData.entries, async site => {
        const { siteHandle } = site;
        try {
          console.log(`[${siteHandle}] starting query (${processed + 1}/${total})`);
          pushLog({
            site: siteHandle,
            ok: true,
            msg: `querying Craft… (${processed + 1}/${total})`,
          });

          let queryResult;
          try {
            queryResult = await importClient.query({
              query: QUERY_LIVE_ALL_MANUALS_FOR_SITEID,
              variables: { siteId: site.siteId }, // scalar, not array
              fetchPolicy: "no-cache", // don’t merge across sites
            });
          } catch (err) {
            console.warn(`[${siteHandle}] query failed (${err.message}); retrying once after ${BUNDLE_QUERY_RETRY_DELAY_MS}ms`);
            pushLog({
              site: siteHandle,
              ok: false,
              msg: `query failed (${err.message}); retrying in ${BUNDLE_QUERY_RETRY_DELAY_MS / 1000}s…`,
            });
            await new Promise(r => setTimeout(r, BUNDLE_QUERY_RETRY_DELAY_MS));
            queryResult = await importClient.query({
              query: QUERY_LIVE_ALL_MANUALS_FOR_SITEID,
              variables: { siteId: site.siteId },
              fetchPolicy: "no-cache",
            });
          }
          const { data } = queryResult;

          /* ---------- build both target paths ---------- */
          const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
          const latestKey = `dataJSON/v1/latest/manualsBundle/${siteHandle}.json`;
          const histKey = `dataJSON/v1/history/${today}/manualsBundle/${siteHandle}.json`;
          const body = JSON.stringify(data || [], null, 2);
          debugLog(`[${siteHandle}] building payload. size=${body.length}`);

          /* ---------- upload BOTH targets & record individual outcome ---------- */
          const targets = [
            { key: latestKey, label: "latest" },
            { key: histKey, label: "history" },
          ];

          const uploads = targets.map(t =>
            s3
              .send(
                new PutObjectCommand({
                  Bucket: buck,
                  Key: t.key,
                  Body: body,
                  ContentType: "application/json",
                  ACL: "public-read",
                })
              )
              .then(() => {
                console.log(`✔︎ ${t.label} → s3://${buck}/${t.key}`);
                pushLog({
                  site: siteHandle,
                  path: t.label,
                  ok: true,
                  msg: `uploaded s3://${buck}/${t.key}`,
                });
              })
              .catch(err => {
                console.error(`✖︎ ${siteHandle} ${t.label} failed`, err);
                pushLog({
                  site: siteHandle,
                  path: t.label,
                  ok: false,
                  msg: err.message,
                });
              })
          );

          /* single await – no longer inside a loop */
          await Promise.allSettled(uploads);
          console.log(`[${siteHandle}] finished all targets`);
        } catch (err) {
          console.error(`upload ${siteHandle} failed`, err);
          pushLog({ site: siteHandle, ok: false, msg: err.message });
        } finally {
          processed += 1;
        }
      });
    } catch (err) {
      console.error("bundle export fatal", err);
      pushLog({ site: "—", ok: false, msg: err.message });
    } finally {
      setExportAllBusy(false);
    }
  };

  const handleUploadAllSitesManualsListToS3 = async () => {
    if (exportAllListsBusy || !sitesData?.entries) return;

    // Reuse the same S3_* URL params as your raw-manuals uploader
    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    setExportAllListsBusy(true);
    const log = [];
    const s3 = new S3Client({
      region: reg,
      credentials: { accessKeyId: key, secretAccessKey: sec },
    });

    try {
      // throttle like the raw manuals uploader
      await asyncPool(4, sitesData.entries, async site => {
        try {
          // 1) Query manuals list for this site
          const { data } = await importClient.query({
            query: QUERY_MANUALS_LIST_FOR_SITEID,
            variables: { siteId: [site.siteId] },
            fetchPolicy: "no-cache",
          });

          // 2) Shape payload exactly like your example requires
          const payload = { data: { manualsList: data?.manualsList ?? [] } };
          const body = JSON.stringify(payload, null, 2);

          // 3) Build target keys   (per your example paths)
          const today = new Date().toISOString().slice(0, 10);
          const latestKey = `dataJSON/v1/latest/manualsList/${site.siteHandle}.json`;
          const histKey = `dataJSON/v1/history/${today}/manualsList/${site.siteHandle}.json`;

          const targets = [
            { key: latestKey, label: "latest" },
            { key: histKey, label: "history" },
          ];

          // 4) Push both targets; record individual results
          const uploads = targets.map(t =>
            s3
              .send(
                new PutObjectCommand({
                  Bucket: buck,
                  Key: t.key,
                  Body: body,
                  ContentType: "application/json",
                  ACL: "public-read",
                })
              )
              .then(() => {
                log.push({
                  site: site.siteHandle,
                  path: t.label,
                  ok: true,
                  msg: `uploaded s3://${buck}/${t.key}`,
                });
              })
              .catch(err => {
                log.push({
                  site: site.siteHandle,
                  path: t.label,
                  ok: false,
                  msg: err.message,
                });
              })
          );

          await Promise.allSettled(uploads);
        } catch (err) {
          log.push({ site: site.siteHandle, ok: false, msg: err.message });
        }
      });
    } catch (err) {
      console.error("ManualsList upload fatal", err);
      log.push({ site: "—", ok: false, msg: err.message });
    } finally {
      setUploadLog(log); // reuse your existing log dialog
      setLogOpen(true);
      setExportAllListsBusy(false);
    }
  };

  const buildS3 = (reg, key, sec) =>
    new S3Client({
      region: reg,
      credentials: { accessKeyId: key, secretAccessKey: sec },
    });
  const putJsonToS3 = (s3, bucket, key, obj) => {
    const body = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);

    return s3.send(
      new PutObjectCommand({
        Bucket: bucket,
        Key: key,
        Body: body,
        ContentType: "application/json",
        ACL: "public-read",
      })
    );
  };
  const latestAndHistoryKeys = (base, siteHandle) => {
    const today = new Date().toISOString().slice(0, 10);
    return [
      { key: `dataJSON/v1/latest/${base}/${siteHandle}.json`, label: "latest" },
      {
        key: `dataJSON/v1/history/${today}/${base}/${siteHandle}.json`,
        label: "history",
      },
    ];
  };

  const handleUploadAllSitesGlobalSetsToS3 = async () => {
    if (exportAllGlobalsBusy || !sitesData?.entries) return;

    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    const siteRecords = uniqueSitesFromDropdown(sitesData.entries).filter(
      s => s?.siteHandle
    ); // we query by handle here

    setExportAllGlobalsBusy(true);
    const s3 = buildS3(reg, key, sec);
    const log = [];

    try {
      await asyncPool(4, siteRecords, async site => {
        const { siteHandle } = site;
        try {
          const { data } = await importClient.query({
            query: GLOBAL_SETS_QUERY,
            variables: { siteHandle: [siteHandle] },
            fetchPolicy: "no-cache",
          });
          // Skip if completely empty (no globals and no variables)
          const empty =
            !data ||
            ((!data.globalSets || data.globalSets.length === 0) &&
              (!data.globalVariables || data.globalVariables.length === 0));
          if (empty) {
            log.push({
              site: siteHandle,
              ok: false,
              msg: "skipped – no globals/variables",
            });
            return;
          }

          const targets = latestAndHistoryKeys("globals", siteHandle);
          const uploads = targets.map(t =>
            putJsonToS3(s3, buck, t.key, { data }).then(
              () =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: true,
                  msg: `uploaded s3://${buck}/${t.key}`,
                }),
              err =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: false,
                  msg: err.message,
                })
            )
          );
          await Promise.allSettled(uploads);
        } catch (err) {
          log.push({ site: siteHandle, ok: false, msg: err.message });
        }
      });
    } finally {
      setUploadLog(log);
      setLogOpen(true);
      setExportAllGlobalsBusy(false);
    }
  };

  const handleUploadAllSitesLegalToS3 = async () => {
    if (exportAllLegalBusy || !sitesData?.entries) return;

    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    const siteRecords = uniqueSitesFromDropdown(sitesData.entries).filter(
      s => s?.siteHandle
    );

    setExportAllLegalBusy(true);
    const s3 = buildS3(reg, key, sec);
    const log = [];

    try {
      await asyncPool(4, siteRecords, async site => {
        const { siteHandle } = site;
        try {
          const { data } = await importClient.query({
            query: LEGAL_QUERY,
            variables: { siteHandle: [siteHandle] },
            fetchPolicy: "no-cache",
          });

          const empty = !data || !data.legal || data.legal.length === 0;
          if (empty) {
            log.push({
              site: siteHandle,
              ok: false,
              msg: "skipped – no legal entries",
            });
            return;
          }

          const targets = latestAndHistoryKeys("legal", siteHandle);
          const uploads = targets.map(t =>
            putJsonToS3(s3, buck, t.key, { data }).then(
              () =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: true,
                  msg: `uploaded s3://${buck}/${t.key}`,
                }),
              err =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: false,
                  msg: err.message,
                })
            )
          );
          await Promise.allSettled(uploads);
        } catch (err) {
          log.push({ site: siteHandle, ok: false, msg: err.message });
        }
      });
    } finally {
      setUploadLog(log);
      setLogOpen(true);
      setExportAllLegalBusy(false);
    }
  };

  const handleUploadAllSitesGlossaryToS3 = async () => {
    if (exportAllGlossaryBusy || !sitesData?.entries) return;

    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    const siteRecords = uniqueSitesFromDropdown(sitesData.entries).filter(
      s => s?.siteHandle
    );

    setExportAllGlossaryBusy(true);
    const s3 = buildS3(reg, key, sec);
    const log = [];

    try {
      await asyncPool(4, siteRecords, async site => {
        const { siteHandle } = site;
        try {
          const { data } = await importClient.query({
            query: GLOSSARY_QUERY,
            variables: { siteHandle: [siteHandle] },
            fetchPolicy: "no-cache",
          });

          const empty = !data || !data.glossary || data.glossary.length === 0;
          if (empty) {
            log.push({
              site: siteHandle,
              ok: false,
              msg: "skipped – no glossary terms",
            });
            return;
          }

          const targets = latestAndHistoryKeys("glossary", siteHandle);
          const uploads = targets.map(t =>
            putJsonToS3(s3, buck, t.key, { data }).then(
              () =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: true,
                  msg: `uploaded s3://${buck}/${t.key}`,
                }),
              err =>
                log.push({
                  site: siteHandle,
                  path: t.label,
                  ok: false,
                  msg: err.message,
                })
            )
          );
          await Promise.allSettled(uploads);
        } catch (err) {
          log.push({ site: siteHandle, ok: false, msg: err.message });
        }
      });
    } finally {
      setUploadLog(log);
      setLogOpen(true);
      setExportAllGlossaryBusy(false);
    }
  };

  /* Upload /v1/languages REST payload (includes isRTL) as a single
     languages.json file — shared across all sites. */
  const handleUploadAllSitesLanguagesToS3 = async () => {
    if (exportAllLanguagesBusy) return;

    const creds = getS3Creds();
    if (!creds) {
      alert(
        "Missing S3 credentials. Set them on the /importexport home page or pass S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET in the URL."
      );
      return;
    }
    const { key, sec, reg, buck } = creds;

    const graphqlUrl = process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL || "";
    const craftBaseUrl = graphqlUrl.replace(/\/api\/?$/, "");
    if (!craftBaseUrl) {
      alert("GATSBY_CRAFT_API_IMPORTEXPORT_URL not configured");
      return;
    }
    const languagesUrl = `${craftBaseUrl}/v1/languages`;
    const token = localStorage.getItem("GATSBY_CRAFT_API_EXPORT_TOKEN") || "";

    setExportAllLanguagesBusy(true);
    const s3 = buildS3(reg, key, sec);
    const log = [];

    try {
      const res = await fetch(languagesUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const languages = Array.isArray(raw) ? raw : raw?.data?.languages || [];
      const payload = { data: { languages } };

      const today = new Date().toISOString().slice(0, 10);
      const targets = [
        { key: `dataJSON/v1/latest/languages.json`, label: "latest" },
        {
          key: `dataJSON/v1/history/${today}/languages.json`,
          label: "history",
        },
      ];
      const uploads = targets.map(t =>
        putJsonToS3(s3, buck, t.key, payload).then(
          () =>
            log.push({
              site: "all",
              path: t.label,
              ok: true,
              msg: `uploaded s3://${buck}/${t.key}`,
            }),
          err =>
            log.push({
              site: "all",
              path: t.label,
              ok: false,
              msg: err.message,
            })
        )
      );
      await Promise.allSettled(uploads);
    } catch (err) {
      log.push({ site: "all", ok: false, msg: err.message });
    } finally {
      setUploadLog(log);
      setLogOpen(true);
      setExportAllLanguagesBusy(false);
    }
  };

  /* overall busy flag */
  const busy =
    uploadBusy ||
    destLoad ||
    liveLoad ||
    sitesLoad ||
    exportAllBusy ||
    exportAllBlocksBusy;

  // Dialog-specific: any S3 upload in flight should show the running state.
  const anyS3UploadBusy =
    exportAllBusy ||
    exportAllListsBusy ||
    exportAllGlobalsBusy ||
    exportAllLegalBusy ||
    exportAllGlossaryBusy ||
    exportAllLanguagesBusy;

  const showTbl = rows.length > 0 && !busy;

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */

  return (
    <Box>
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 2,
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <GTranslateIcon fontSize="medium" />
          <Typography variant="h4">
            DigiGuide Advanced Import & Export
          </Typography>
          <GTranslateIcon fontSize="medium" />
        </Box>
      </Box>

      {/* ─── file picker + export buttons ─────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          p: 2,
        }}
      >
        <Button
          component="label"
          variant="contained"
          startIcon={<UploadIcon />}
        >
          Choose Import File
          <input type="file" accept=".csv" hidden onChange={onFile} />
        </Button>
        <TextField disabled size="small" value={fileName} sx={{ width: 240 }} />

        {/* site and manual selector */}
        <Box
          sx={{
            p: 1,
            display: "flex",
            gap: 2,
            justifyContent: "flex-end", // right-align
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* ▼ site dropdown */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="site-dd-label">Sites of this Manual</InputLabel>
            <Select
              labelId="site-dd-label"
              label="Sites of this Manual"
              value={siteChoice}
              onChange={e => setSiteChoice(e.target.value)}
            >
              {sitesData?.entries?.map(site => (
                <MenuItem
                  key={site.siteId}
                  value={site.siteId}
                  sx={
                    site.siteId === siteIdProp ? { fontWeight: 600 } : undefined
                  }
                >
                  {site.language} ⇨ {site.siteHandle} {`(${site.siteId})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ↻ go button */}
          <Button variant="outlined" disabled={!siteChoice} onClick={goToSite}>
            Load Site
          </Button>

          {/* ↩ back to manual chooser */}
          <Button variant="outlined" color="secondary" href="/importexport">
            Switch Manual
          </Button>
        </Box>

        {/* ▼▼▼  CHANGED:  added “Export:” label + renamed buttons  ▼▼▼ */}
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            p: 1,
            ml: "auto",
            bgcolor: "background.paper",
          }}
        >
          {/* label with 3-sided border */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              px: 1.5,
              borderStyle: "solid",
              borderWidth: "1px 0 1px 1px", // top, right, bottom, left
              borderColor: "divider",
              borderRadius: "4px 0 0 4px",
            }}
          >
            Export
            <ArrowIcon />
          </Typography>
          {/* single-site exports */}
          <Button variant="contained" onClick={handleCsvExport}>
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            CSV All Entries
          </Button>
          <Button variant="contained" onClick={handleXliffExport}>
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            XLIFF All Entries
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const m = liveManualData.entry;
              download(
                `${safeTitle(m.userTitle)}_${m.id}_${m.language}_${
                  m.siteId
                }__indesign_${tsStamp()}.xml`,
                exportToInDesignXML(
                  liveManualData.entry,
                  liveManualData?.globalVariables || []
                )
              );
            }}
          >
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            InDesign XML
          </Button>
          <Button
            variant="contained"
            onClick={handleBlocksReqTranslationExport}
          >
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            CSV Blocks Req TX
          </Button>
          {/* all-site exports */}
          <Button
            variant="contained"
            color="secondary"
            disabled={sitesLoad || exportAllBusy}
            onClick={handleExportAllSitesCsv}
          >
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            CSV All Sites All Entries
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={sitesLoad || exportAllBlocksBusy}
            onClick={handleExportAllSitesBlocksCsv}
          >
            <FileDownloadIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            CSV All Sites Blocks Req TX
          </Button>
          {/* upload all sites manuals to s3 */}
          <Button
            variant="contained"
            sx={{ bgcolor: "#097", "&:hover": { bgcolor: "#086" } }}
            disabled={sitesLoad || exportAllBusy}
            onClick={handleUploadAllSitesRawManualsToS3}
          >
            Raw manuals&nbsp;(all&nbsp;sites)&nbsp;➜&nbsp;S3
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#0a6", "&:hover": { bgcolor: "#095" } }}
            disabled={sitesLoad || exportAllListsBusy}
            onClick={handleUploadAllSitesManualsListToS3}
          >
            ManualsList&nbsp;(all&nbsp;sites)&nbsp;➜&nbsp;S3
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#5a6", "&:hover": { bgcolor: "#4a5" } }}
            disabled={sitesLoad || exportAllGlobalsBusy}
            onClick={handleUploadAllSitesGlobalSetsToS3}
          >
            globals&nbsp;(all&nbsp;sites)&nbsp;➜&nbsp;S3
          </Button>

          <Button
            variant="contained"
            sx={{ bgcolor: "#6a5", "&:hover": { bgcolor: "#5a4" } }}
            disabled={sitesLoad || exportAllLegalBusy}
            onClick={handleUploadAllSitesLegalToS3}
          >
            legal&nbsp;(all&nbsp;sites)&nbsp;➜&nbsp;S3
          </Button>

          <Button
            variant="contained"
            sx={{ bgcolor: "#596", "&:hover": { bgcolor: "#485" } }}
            disabled={sitesLoad || exportAllGlossaryBusy}
            onClick={handleUploadAllSitesGlossaryToS3}
          >
            glossary&nbsp;(all&nbsp;sites)&nbsp;➜&nbsp;S3
          </Button>

          <Button
            variant="contained"
            sx={{ bgcolor: "#486", "&:hover": { bgcolor: "#375" } }}
            disabled={exportAllLanguagesBusy}
            onClick={handleUploadAllSitesLanguagesToS3}
          >
            languages.json&nbsp;➜&nbsp;S3
          </Button>
        </Paper>

        {busy && <LinearProgress sx={{ flexBasis: "100%", mt: 1 }} />}
        {csvErr && (
          <Typography color="error" sx={{ flexBasis: "100%", mt: 1 }}>
            {csvErr}
          </Typography>
        )}
      </Box>

      {/* filter + toggle bar */}
      <Box
        sx={{
          bgcolor: "secondary.main",
          color: "secondary.contrastText",
          p: 1,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={filterNeedsTranslation}
              onChange={() => setFilterNeeds(p => !p)}
            />
          }
          label="Only show blocks req translation"
        />
        <FormControlLabel
          control={
            <Switch
              checked={filterChanged}
              onChange={() => setFilterChanged(p => !p)}
            />
          }
          label="Only show changed blocks"
        />
      </Box>

      {/* main paper */}
      <Paper elevation={3} sx={{ m: 2, p: 2 }}>
        {showTbl ? (
          <div className="preview-mode">
            {/* bulk-select toolbar */}
            <Box sx={{ mb: 1, display: "flex", gap: 2 }}>
              <Button size="small" onClick={handleSelectAll}>
                Select all
              </Button>
              <Button size="small" onClick={handleSelectNone}>
                Select none
              </Button>
              <Button size="small" onClick={handleSelectModified}>
                Select modified
              </Button>
              <Button size="small" onClick={handleSelectNeedsTx}>
                Select req translation
              </Button>
            </Box>

            <TableView
              rows={visibleRows}
              onToggle={toggle}
              needsTranslation={needsTranslation}
              varMap={varMap}
            />
          </div>
        ) : (
          liveManualData?.entry && (
            <>
              <Header onFirstPage />
              <Cover chapters={manual.chapters} />
              <Typography>
                Loading Current Manual and Primary-site (en) Manual for
                comparison…
              </Typography>
            </>
          )
        )}

        {rows.length > 0 && (
          <SimplifiedDiffContent
            rows={rows}
            setRows={setImportRows}
            siteId={destSiteId}
          />
        )}
      </Paper>
      {/* ─────────────── Upload-log dialog ─────────────── */}
      <Dialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {anyS3UploadBusy ? "Upload running…" : "Upload results"}
        </DialogTitle>
        {anyS3UploadBusy && <LinearProgress />}
        <DialogContent dividers>
          {uploadLog.length === 0 && anyS3UploadBusy && (
            <Typography variant="body2" color="text.secondary">
              Starting…
            </Typography>
          )}
          {uploadLog.map((r, i) => (
            <Box
              key={`${r.site}-${r.path}-${i}`}
              sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.5 }}
            >
              {r.ok ? (
                <CheckIcon sx={{ color: "green" }} />
              ) : (
                <ErrorIcon color="error" />
              )}
              <Typography variant="body2">
                <strong>{r.site}</strong> — {r.msg}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)} disabled={anyS3UploadBusy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

SimplifiedManualImportExportPage.propTypes = {
  data: PropTypes.object,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  error: PropTypes.object,
};
